const GOAL_STATE_VERSION = 1;
const MAX_KEYWORDS = 16;
const MAX_RECENT_GOAL_SUMMARIES = 6;

const clipText = (value = '', limit = 220) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  const maxChars = Math.max(32, Number(limit || 220));
  if (!text) return '';
  return text.length <= maxChars ? text : `${text.slice(0, maxChars - 3).trimEnd()}...`;
};

const normalizeId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const dedupeKeywords = (values = []) => {
  const seen = new Set();
  const result = [];

  for (const raw of Array.isArray(values) ? values : []) {
    const token = String(raw || '').trim();
    if (!token) continue;
    const key = token.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(token);
    if (result.length >= MAX_KEYWORDS) break;
  }

  return result;
};

const dedupeTextList = (values = [], maxItems = MAX_RECENT_GOAL_SUMMARIES, maxChars = 220) => {
  const seen = new Set();
  const result = [];
  const max = Math.max(1, Number(maxItems || MAX_RECENT_GOAL_SUMMARIES));

  for (const raw of Array.isArray(values) ? values : []) {
    const text = clipText(raw || '', maxChars);
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(text);
    if (result.length >= max) break;
  }

  return result;
};

const extractKeywords = (userText = '') => {
  const text = String(userText || '');
  if (!text) return [];

  const uppercaseTokens = Array.from(text.matchAll(/\b[A-Z][A-Z0-9&._-]{1,}\b/g)).map(match => match[0]);
  const titleTokens = Array.from(text.matchAll(/\b[A-Z][a-z][A-Za-z0-9&._-]{2,}\b/g)).map(match => match[0]);
  const longTokens = Array.from(text.matchAll(/\b[a-zA-Z][a-zA-Z0-9&._-]{4,}\b/g)).map(match => match[0]);

  return dedupeKeywords([...uppercaseTokens, ...titleTokens, ...longTokens]);
};

const detectExhaustiveScope = (userText = '') => {
  const value = String(userText || '').toLowerCase();
  if (!value) return false;
  return /\blist all\b|\ball\b|\bevery\b|\bcomplete\b|\bentire\b|\bfull\b|\bexhaustive\b/.test(value);
};

export const normalizeGoalState = (goalState = null) => {
  const source = goalState && typeof goalState === 'object' ? goalState : {};
  const targets = source.targets && typeof source.targets === 'object' ? source.targets : {};
  const constraints = source.constraints && typeof source.constraints === 'object' ? source.constraints : {};

  const linkedDocIds = Array.isArray(targets.linkedDocIds)
    ? Array.from(new Set(targets.linkedDocIds.map(id => normalizeId(id)).filter(Boolean)))
    : [];

  return {
    version: Number(source.version || GOAL_STATE_VERSION) || GOAL_STATE_VERSION,
    turns: Math.max(0, Number(source.turns || 0)),
    currentIntentKey: String(source.currentIntentKey || 'ask').trim() || 'ask',
    currentMode: String(source.currentMode || 'Explore').trim() || 'Explore',
    summary: clipText(source.summary || '', 280),
    recentGoalSummaries: dedupeTextList(source.recentGoalSummaries || [], MAX_RECENT_GOAL_SUMMARIES, 180),
    lastUserTextPreview: clipText(source.lastUserTextPreview || '', 240),
    keywords: dedupeKeywords(source.keywords),
    constraints: {
      exhaustive: Boolean(constraints.exhaustive)
    },
    targets: {
      activeDocId: normalizeId(targets.activeDocId),
      templateDocId: normalizeId(targets.templateDocId),
      linkedDocIds
    },
    updatedAt: String(source.updatedAt || '').trim() || null
  };
};

export const mergeGoalState = ({
  previousGoalState = null,
  userText = '',
  intent = null,
  mode = 'Explore',
  contextPlan = null
} = {}) => {
  const previous = normalizeGoalState(previousGoalState);
  const contract = intent?.contract && typeof intent.contract === 'object' ? intent.contract : {};
  const contractPrimary = dedupeKeywords(contract.primaryKeywords || []);
  const contractSecondary = dedupeKeywords(contract.secondaryKeywords || []);
  const nextKeywords = dedupeKeywords([
    ...previous.keywords,
    ...contractPrimary,
    ...contractSecondary,
    ...extractKeywords(userText)
  ]);
  const nextExhaustive = previous.constraints.exhaustive || detectExhaustiveScope(userText);

  const activeDocId = normalizeId(contextPlan?.activeDocId) || previous.targets.activeDocId;
  const templateDocId = normalizeId(contextPlan?.templateDocId) || previous.targets.templateDocId;
  const linkedDocIdsFromContext = Array.isArray(contextPlan?.linkedDocIds)
    ? Array.from(new Set(contextPlan.linkedDocIds.map(id => normalizeId(id)).filter(Boolean)))
    : null;
  const linkedDocIds = Array.isArray(linkedDocIdsFromContext) && linkedDocIdsFromContext.length > 0
    ? linkedDocIdsFromContext
    : previous.targets.linkedDocIds;

  const intentKey = String(intent?.key || previous.currentIntentKey || 'ask').trim() || 'ask';
  const resolvedMode = String(mode || previous.currentMode || 'Explore').trim() || 'Explore';
  const preview = clipText(userText || '', 240);
  const nextSummary = clipText(
    String(contract.goalSummary || '').trim() || `${intentKey}: ${preview}`,
    280
  );
  const recentGoalSummaries = dedupeTextList([
    nextSummary,
    previous.summary,
    ...(Array.isArray(previous.recentGoalSummaries) ? previous.recentGoalSummaries : [])
  ], MAX_RECENT_GOAL_SUMMARIES, 180);

  return {
    ...previous,
    version: GOAL_STATE_VERSION,
    turns: previous.turns + 1,
    currentIntentKey: intentKey,
    currentMode: resolvedMode,
    summary: nextSummary,
    recentGoalSummaries,
    lastUserTextPreview: preview,
    keywords: nextKeywords,
    constraints: {
      exhaustive: nextExhaustive
    },
    targets: {
      activeDocId,
      templateDocId,
      linkedDocIds
    },
    updatedAt: new Date().toISOString()
  };
};

export const serializeGoalStateForPrompt = (goalState = null) => {
  const normalized = normalizeGoalState(goalState);
  if (!normalized || normalized.turns === 0) return '';

  const lines = [
    '=== CONVERSATION MEMORY ===',
    `Turn: ${normalized.turns} | Intent: ${normalized.currentIntentKey} | Mode: ${normalized.currentMode}`
  ];

  if (Array.isArray(normalized.keywords) && normalized.keywords.length > 0) {
    lines.push(`Keywords: ${normalized.keywords.slice(0, 12).join(', ')}`);
  }

  if (normalized.summary) {
    lines.push(`Summary: ${normalized.summary}`);
  }

  if (Array.isArray(normalized.recentGoalSummaries) && normalized.recentGoalSummaries.length > 1) {
    lines.push(`Recent goals: ${normalized.recentGoalSummaries.slice(0, 4).join(' | ')}`);
  }

  return lines.join('\n');
};

export const summarizeGoalStateForAudit = (goalState = null) => {
  const normalized = normalizeGoalState(goalState);
  return {
    turns: normalized.turns,
    currentIntentKey: normalized.currentIntentKey,
    currentMode: normalized.currentMode,
    exhaustive: Boolean(normalized.constraints.exhaustive),
    keywordCount: Array.isArray(normalized.keywords) ? normalized.keywords.length : 0,
    keywords: Array.isArray(normalized.keywords) ? normalized.keywords.slice(0, 8) : [],
    recentGoalCount: Array.isArray(normalized.recentGoalSummaries)
      ? normalized.recentGoalSummaries.length
      : 0,
    targets: {
      activeDocId: normalized.targets.activeDocId,
      templateDocId: normalized.targets.templateDocId,
      linkedDocCount: Array.isArray(normalized.targets.linkedDocIds)
        ? normalized.targets.linkedDocIds.length
        : 0
    },
    summary: normalized.summary,
    updatedAt: normalized.updatedAt
  };
};
